import React from 'react';
import {
  Box,
  Chip,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material';
import LoopIcon from '@mui/icons-material/Loop';
import LooksOneIcon from '@mui/icons-material/LooksOne';

import {
  algorithmCatalog,
  ALGO_HEALTH,
  ALGO_TOPOLOGY,
  ALGO_POWER,
  ALGO_CHANNEL,
  ALGO_BACKUP,
} from './algorithmCatalog';

// MetaPipelineView is the rich "what is the meta-analyser going to
// do?" explainer for the Optimize (Meta) panel. It is rendered inside
// the AnalyzerHeader's "How it works" dropdown via the
// `extraHelpContent` slot.
//
// The pipeline definition mirrors `internal/factory/algo/optimize.go`:
//
//   Health (once, advisory)
//   loop i = 1..max_iterations:
//     Topology (single-pass local search)
//     TX Power (per-link min-power)
//     Channels (DSATUR)
//   P10 Guard (prune topology actions if p10 regressed)
//   Backup parents (once, advisory)

// Small bullet list used inside step explainers.
const UL = ({ children }) => (
  <ul style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ul>
);

const PIPELINE = [
  {
    id: ALGO_HEALTH,
    phase: 'once',
    role: 'Triage snapshot',
    goal: 'Surface health issues before any change is proposed.',
    explainer: (
      <>
        <strong>Classic:</strong> latency, jitter, TX-failure %, uplink RSSI, hop depth, offline.
        <br /><br />
        <strong>With rf_telemetry — four extra issue types:</strong>
        <UL>
          <li><strong>airtime_saturation</strong> — channel busy ≥ 60%</li>
          <li><strong>external_interference</strong> — foreign airtime ≥ 25%</li>
          <li><strong>rf_errors</strong> — FCS or ACK failure ≥ 5%</li>
          <li><strong>link_retries_burst</strong> — per-station retry rate ≥ 20%</li>
        </UL>
        Each issue hints which analyser to run next (e.g. external_interference → channel,
        link_retries_burst → topology). Never acts. When "Focus on Health" is on, the hot-list
        seeds the loop.
      </>
    ),
  },
  {
    id: ALGO_TOPOLOGY,
    phase: 'loop',
    role: 'Re-parenting pass',
    goal: 'Raise weak-path throughput p10 by moving nodes to better-rate parents.',
    explainer: (
      <>
        <strong>Cost ladder</strong> (best signal wins):
        <UL>
          <li>Child STA uplink profile — MCS / NSS / BW → estimated kbps</li>
          <li>Parent AP delivered kbps + loss% + retry%</li>
          <li>Ack_Signal_Avg</li>
          <li>Scan RSSI</li>
        </UL>
        <strong>With rf_telemetry:</strong>
        <UL>
          <li>Prefers <strong>phy_rate_mbps</strong> over tx_rate.bitrate</li>
          <li>Uses recent per-window loss/retry deltas — not cumulative ratios</li>
          <li>
            Marks idle stations (inactive_ms &gt; 600 s) as stale — suppresses their loss/retry
            penalty so a silent client doesn&apos;t look like a bad link
          </li>
          <li>
            Nodes with <em>link_retries_burst</em> get a 30% lower improvement threshold, so
            degrading links are re-parented more aggressively
          </li>
        </UL>
        <strong>With iperf measurements:</strong>
        <UL>
          <li>
            Candidate parents whose path-to-CAP is a measured bottleneck (&gt;30% degradation) get a
            heavy cost penalty — effectively disqualified unless no alternative exists
          </li>
          <li>
            Proposed kbps capped at the measured E2E throughput of the parent&apos;s path — no
            advertising a PHY rate the uplink can&apos;t carry
          </li>
          <li>
            <em>measured_bottleneck</em> issues surface for edges below 50 Mbps that the planner
            can&apos;t re-parent (no better candidate available)
          </li>
        </UL>
        Hypothetical candidates estimated from the would-be parent&apos;s children scaled by ΔRSSI.
        Capped by blast radius; moved nodes are pinned.
      </>
    ),
  },
  {
    id: ALGO_POWER,
    phase: 'loop',
    role: 'TX-power trim',
    goal: 'Drop overpowered radios to reduce co-channel interference.',
    explainer: (
      <>
        <UL>
          <li>
            <strong>Downlink</strong> — per AP radio, recommend minimum TX that keeps the weakest
            child healthy.
          </li>
          <li>
            <strong>Uplink</strong> — per child STA radio, check if TX exceeds actual path loss.
            Uplink actions carry 1.5× severity.
          </li>
        </UL>
        <strong>With rf_telemetry — TX-drop is vetoed when:</strong>
        <UL>
          <li>ack_failure_rate ≥ 5% (link is already struggling)</li>
          <li>tx_airtime_pct ≥ 40% (radio is heavily loaded)</li>
        </UL>
        <strong>With iperf measurements:</strong>
        <UL>
          <li>
            Power-down is <strong>suppressed</strong> on links where iperf measured &gt;200 Mbps with
            &lt;1% loss — the link is provably healthy regardless of RSSI math. The issue still
            surfaces with a SuppressedReason.
          </li>
          <li>
            <em>interference_suspected</em> issues fire when RSSI is strong (&gt;-65 dBm both ways)
            but measured throughput is low (&lt;100 Mbps) — indicates off-channel interference that
            the channel analyser should address.
          </li>
        </UL>
        Vetoed actions still surface as issues with a <em>SuppressedReason</em>. Double-drop guard:
        no radio loses more than ~9 dB across the full plan.
      </>
    ),
  },
  {
    id: ALGO_CHANNEL,
    phase: 'loop',
    role: 'Channel re-colouring',
    goal: 'Cut conflict severity — separating own load from external interference.',
    explainer: (
      <>
        <UL>
          <li>Build interference graph from GPS + spectral overlap.</li>
          <li>
            Filter with neighbour-scan reports — drop RF-isolated pairs; fall back to GPS-only if
            scan data is missing.
          </li>
          <li>Re-assign channels with DSATUR colouring.</li>
        </UL>
        <strong>Bandwidth-honest severity:</strong>
        <UL>
          <li>
            Each AP is modelled as a real bonded signal — two HT80 APs in the same 80 MHz block
            (e.g. ch 36 vs ch 48 on 5 GHz both occupy 5170-5250 MHz) score full overlap. Moving
            primaries within the same block is no longer mistaken for an improvement.
          </li>
          <li>
            <strong>Hard same-block reject:</strong> the picker never picks a candidate that stays
            in the same bonded block as a critical neighbour. Forces a real move or a bandwidth
            downgrade.
          </li>
        </UL>
        <strong>Bandwidth awareness:</strong>
        <UL>
          <li>
            Candidates filtered by max channel bandwidth — e.g. ch 165 (20 MHz only) excluded at
            HT80.
          </li>
          <li>
            When high-severity conflicts persist, tries <strong>stepwise bandwidth
            downgrades</strong>: first HT80 → HT40, then HT40 → HT20 if 40 MHz wasn&apos;t
            enough. Penalty scales with the BW drop — same-BW moves always win when comparable.
          </li>
        </UL>
        <strong>With rf_telemetry:</strong>
        <UL>
          <li>
            Severity uses <strong>ForeignAirtimeFraction</strong> (= channel_busy − own TX/RX) —
            separates &quot;my children are loading this channel&quot; from &quot;an external AP is
            hammering it&quot;
          </li>
          <li>
            Candidate channels demoted by FCS-error penalty when fcs_error_rate &gt; 5% — catches
            non-WiFi interference (microwave, radar)
          </li>
          <li>Mixed fleets: each side falls back independently when rf_telemetry is absent</li>
        </UL>
        <strong>With iperf measurements:</strong>
        <UL>
          <li>
            Issues whose affected APs sit on a <strong>degraded</strong> iperf path (&gt;20%
            degradation) get severity &times;1.5 (capped at 1.0) — prioritises conflicts that are
            actually hurting throughput
          </li>
          <li>
            Issues whose affected APs sit only on <strong>healthy</strong> iperf paths (&lt;5%
            degradation) get severity &times;0.5 — deprioritises conflicts that aren&apos;t causing
            real harm
          </li>
        </UL>
        Severity also scaled by worst-of-two mean child uplink MCS. After each pass, the next
        Topology iteration sees the updated RF environment (C→A coupling).
      </>
    ),
  },
  {
    id: ALGO_BACKUP,
    phase: 'once',
    role: 'Failover advisory',
    goal: 'Record alternative parent paths per Node for failover reasoning.',
    explainer: (
      <>
        <UL>
          <li>Runs Yen&apos;s k-shortest paths to a CAP for every Node.</li>
          <li>
            Edge weights use <strong>EffectiveLinkCostV2</strong> — incorporates phy_rate_mbps and
            recent loss/retry deltas when rf_telemetry is available.
          </li>
          <li>
            Pure advisory — no actions emitted. Use to check: does every Node have at least one
            decent backup path?
          </li>
        </UL>
      </>
    ),
  },
];

const PhaseChip = ({ phase }) => {
  if (phase === 'loop') {
    return (
      <Tooltip title="Repeats every iteration of the descent loop">
        <Chip
          size="small"
          variant="outlined"
          color="primary"
          icon={<LoopIcon sx={{ fontSize: 14 }} />}
          label="Loop"
          sx={{ height: 22 }}
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Runs exactly once per plan">
      <Chip
        size="small"
        variant="outlined"
        icon={<LooksOneIcon sx={{ fontSize: 14 }} />}
        label="Once"
        sx={{ height: 22 }}
      />
    </Tooltip>
  );
};

const MetaPipelineView = () => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}
    >
      Pipeline
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
      <strong>Health</strong> once → loop[<strong>Topology → TX Power → Channels</strong>]{' '}
      → <strong>P10 Guard</strong> → <strong>Backup</strong> once.
    </Typography>

    {/* nonLinear + activeStep=-1 keeps every step visually neutral
        (no "current step" highlight). We only want the connector
        line + numbered icons; this is documentation, not progress. */}
    <Stepper
      orientation="vertical"
      nonLinear
      activeStep={-1}
      sx={{
        '& .MuiStepLabel-iconContainer': { pr: 1.5 },
        '& .MuiStepConnector-line': { borderColor: 'divider' },
      }}
    >
      {PIPELINE.map((step, idx) => {
        const cat = algorithmCatalog[step.id];
        const Icon = cat?.icon;
        return (
          <Step key={step.id} expanded active={false} completed={false}>
            <StepLabel
              StepIconComponent={() => (
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: 1,
                    bgcolor: step.phase === 'loop' ? 'primary.main' : 'action.selected',
                    color: step.phase === 'loop' ? 'primary.contrastText' : 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: 13,
                    position: 'relative',
                  }}
                >
                  {Icon ? <Icon fontSize="small" /> : idx + 1}
                </Box>
              )}
            >
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {idx + 1}. {cat?.title || step.id}
                </Typography>
                <PhaseChip phase={step.phase} />
                {cat?.advisory && (
                  <Tooltip title="Advisory — produces a reading but no actions">
                    <Chip size="small" variant="outlined" label="Advisory" sx={{ height: 22 }} />
                  </Tooltip>
                )}
                <Typography variant="caption" color="text.secondary">
                  {step.role}
                </Typography>
              </Stack>
            </StepLabel>
            <StepContent
              sx={{
                ml: '15px',
                borderLeft: '1px dashed',
                borderColor: 'divider',
                pl: 2,
                pt: 0.5,
                pb: 1.5,
              }}
            >
              <Box sx={{ display: 'grid', gap: 1.25 }}>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}
                  >
                    Algorithm
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {cat?.algorithm || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}
                  >
                    Goal
                  </Typography>
                  <Typography variant="body2">{step.goal}</Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}
                  >
                    What it does
                  </Typography>
                  {/* component="div" allows block-level children (ul/li) */}
                  <Typography variant="body2" component="div">
                    {step.explainer}
                  </Typography>
                </Box>
              </Box>
            </StepContent>
          </Step>
        );
      })}
    </Stepper>

    <Box
      sx={{
        mt: 1,
        pt: 1.5,
        borderTop: '1px dashed',
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        The middle three steps repeat up to <strong>max iterations</strong> times until none of
        them produces a meaningful improvement (each step has its own ε floor). After the loop,
        the <strong>P10 Guard</strong> checks if the combined topology actions regressed the
        10th-percentile path throughput; if so, it prunes the weakest actions until throughput
        recovers. Then Backup runs once on the final tree. The pipeline only <em>computes</em> a
        plan — nothing is changed on the network until you press <strong>Apply</strong>.
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        <strong>Iperf anchoring:</strong> when &ldquo;Use iperf measurements&rdquo; is on and a
        site-wide iperf run has any topology overlap, each stage replaces its theoretical estimates
        with real measured throughput for the edges and paths that were tested. Unmeasured edges fall
        back to normal logic. The match % in the dropdown tells you how much of the run&apos;s data
        applies. No overlapping run? The pipeline runs unmodified with theoretical estimates only.
      </Typography>
    </Box>
  </Box>
);

export default MetaPipelineView;
